import React, { CSSProperties, FC, useCallback, useContext, useMemo, useRef, useState } from "react";
import cn from "classnames";
import { BiLinkExternal } from 'react-icons/bi';
import TruncatedText from "react-dotdotdot";
import Http from "../../../core/fetch";
import { iLTO } from "@models/LTO";
import { SectionEnum } from "@enums";
import useWindowSizeDebounced from "../../../core/hooks/useWindowSizeDebounced";
import { ChainDetailsDataContext } from "@pages/ChainPage/ChainPage.helpers";
import { getFormattedDate } from "@helpers";
import { isAvailable, PermissionLevelEnum } from "@models/PermissionSection";
import Scores from "@pages/ChainPage/Sections/LTO/LTOItem/Scores/Scores";
import "./LTOItem.styles.scss";
import {
    AnalyticsAction,
    AnalyticsAttribute,
    FoodProfileDownloadLocation,
    getAnalyticsComputedData
} from "@models/AnalyticsAttributes";
import http from "@core/fetch";
import PermissionOverlay from "@pages/Food/components/PermissionOverlay/PermissionOverlay";
import { toast } from "react-toastify";
import { FCX, includesInLC, Tooltip, useUuid } from "@datassential/platform-ui-lib";
import { FaCertificate, FaClock, FaStickyNote, FaSyncAlt } from 'react-icons/fa';

interface iProps {
    data: iLTO;
    style?: {
        root?: CSSProperties,
        wrapper?: CSSProperties,
    };
    location: 'chain page' | 'food profile';
    testId?: string;
}

const StatusIcon: FCX<{
    status: string;
}> = ({
    status,
    className,
    style,
}) => {
    let Component;

    if (includesInLC(status, 'limited')) {
        Component = FaClock;
    } else if (includesInLC(status, 'new')) {
        Component = FaCertificate;
    } else if (includesInLC(status, 'return')) {
        Component = FaSyncAlt;
    }

    if (!Component) return null;

    return <Component style={style} className={className}/>;
}

const LTOItem: FC<iProps> = (
    {
        data,
        style,
        location,
        testId,
    }
) => {
    const uuid = useUuid();
    const [isDownloading, setIsDownloading] = useState(false);
    const leadSource = useMemo(() => {
        switch (location) {
            case 'chain page':
                return SectionEnum.LtoTileChainPage;
            case 'food profile':
                return SectionEnum.LtoTileFoodProfile;
            default:
                return '';
        }
    }, [location]);

    const handleUnlockRequest = () => {
        Http.get('unlock request', { leadSource, ltoName: name }, {}, "none");
        toast.info('Request has been sent.');
    }

    const {
        name,
        chainName,
        date,
        description,
        imageUrl,
        isScoresItem,
        price,
        scoresData,
        note,
        status,
    } = data;

    const {
        permissionLevel,
        data: {
            downloadPptLink,
            indexes,
            score,
            viability,
            link,
        }
    } = scoresData;

    const itemRef = useRef<HTMLDivElement>(null);
    const [wWidth] = useWindowSizeDebounced(1000);
    const {
        data: {
            mainData: {
                logoUrl
            }
        }
    } = useContext(ChainDetailsDataContext);

    const isHiddenScores = !isScoresItem;
    const descriptionLines = isHiddenScores ? 9 : 3;

    const classnames = cn(
        "LTOItem",
        !!link ? "is-clickable" : "",
        !isAvailable(scoresData) && "is-blocked",
    );

    const renderName = useMemo(
        () => {
            /* @ts-ignore */
            const text = <TruncatedText clamp={2}>{name}</TruncatedText>;
            if (isScoresItem && !!link) {
                return (
                    <a
                        href={link}
                        target="_blank"
                        rel="noreferrer"
                        {...getAnalyticsComputedData({
                            [AnalyticsAttribute.ClickTitle]: 'Scores Redirect',
                        })}
                    >
                        {text}
                    </a>
                );
            }

            return text;
        },
        [name, isScoresItem, link]
    );

    const downloadFile = useCallback(() => {
        setIsDownloading(true);
        toast.info('Downloading...');
        http.downloadFileXHRFromUrl('GET', downloadPptLink)
            .then(() => {
                toast.success('Download Successful');
                setIsDownloading(false);
            });
    }, [downloadPptLink]);

    return (
        <div
            className={classnames}
            style={style?.root}
            ref={itemRef}
            data-testid={testId}
        >

            <div className="LTOItem__wrapper" style={style?.wrapper}>
                <div className="LTOItem__header">
                    <div
                        className="LTOItem__sub-title"
                        title={chainName}
                        data-testid="textLtoChainName"
                    >
                        {chainName}
                    </div>
                    <div
                        className="LTOItem__title"
                        title={name}
                        data-testid="textLtoTitle"
                    >
                        {renderName}
                    </div>
                </div>

                <div className={cn(
                    'LTOItem__content',
                    isHiddenScores && "is-blank",
                    !!status && "has-status",
                )}>
                    <div className="LTOItem__image-wrapper">
                        <img
                            src={imageUrl || logoUrl}
                            className={`LTOItem__image ${imageUrl ? "is-cover" : ""}`}
                            alt=""
                            data-testid="imageLtoThumbnail"
                        />
                        {isScoresItem && permissionLevel === PermissionLevelEnum.Full && (!!downloadPptLink || !!link) && (
                            <div className="LTOItem__menu">
                                {!!link && (
                                    <a
                                        href={link}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="LTOItem__menu-item clickable"
                                        {...getAnalyticsComputedData({
                                            [AnalyticsAttribute.ClickTitle]: 'Scores Redirect',
                                        })}
                                        data-testid="buttonLtoExternalLink"
                                    >
                                        VIEW ITEM DETAILS
                                        <BiLinkExternal style={{
                                            position: 'absolute',
                                            right: 6,
                                            width: 16,
                                            height: 16,
                                            top: 7,
                                        }}/>
                                    </a>
                                )}
                                {!!downloadPptLink && (
                                    <div
                                        className={cn(
                                            "LTOItem__menu-item clickable",
                                            isDownloading && 'is-loading',
                                        )}
                                        onClick={downloadFile}
                                        {...getAnalyticsComputedData({
                                            [AnalyticsAttribute.Action]: AnalyticsAction.Download,
                                            [AnalyticsAttribute.DownloadLocation]: FoodProfileDownloadLocation.FoodProfileLto,
                                            [AnalyticsAttribute.ClickTitle]: 'Chains Featuring Keyword - Download',
                                        })}
                                        data-testid="buttonLtoDownload"
                                    >
                                        {isDownloading ? 'downloading...' : 'DOWNLOAD REPORT'}
                                        {isDownloading && <div className="ProgressLoader is-inside-button"/>}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="LTOItem__description-wrapper">
                        <div
                            className="LTOItem__description"
                            data-testid="textLtoDescriptionExpanded"
                        >
                            {/* @ts-ignore */}
                            <TruncatedText
                                clamp={descriptionLines}
                                key={wWidth}
                            >
                                {description}
                            </TruncatedText>
                        </div>
                        <div
                            className="LTOItem__description LTOItem__description-hover"
                            data-testid="textLtoDescriptionExpanded"
                        >
                            {description}
                        </div>
                    </div>
                </div>
                {!!status && (
                    <div
                        className="LTOItem__status"
                    >
                        <StatusIcon status={status}/>&nbsp;&nbsp;{status}
                        {!!note && (
                            <>
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        bottom: 0,
                                        right: 8,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                    data-tooltip-id={`tile-${uuid}-status-note`}
                                >
                                    <FaStickyNote/>
                                </div>
                                <Tooltip
                                    id={`tile-${uuid}-status-note`}
                                    place={'top-end'}
                                    variant="light"
                                    positionStrategy={'fixed'}
                                    style={{ zIndex: 4, fontSize: 14, maxWidth: 260 }}
                                >
                                    {note}
                                </Tooltip>
                            </>
                        )}
                    </div>
                )}
                <div
                    className={`LTOItem__scores ${isHiddenScores ? "is-blank" : ""}`}
                >
                    {isScoresItem && (
                        <>
                            {isAvailable(scoresData) ? (
                                <Scores data={indexes} score={score} title={viability}/>
                            ) : (

                                <div className="Scores">
                                    <PermissionOverlay
                                        onContactUsClick={handleUnlockRequest}
                                        contentStyleModifiers={['small-text', 'small-p', 'no-bg']}
                                    >
                                        <div>Upgrade your subscription to discover</div>
                                        <div> how consumers feel about this item.</div>
                                    </PermissionOverlay>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="LTOItem__footer">
                    <div
                        className="LTOItem__date"
                        data-testid="textLtoDate"
                    >
                        {getFormattedDate(date)}
                    </div>
                    <div
                        className="LTOItem__price"
                        data-testid="textLtoPrice"
                    >
                        {price}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(LTOItem);
